import React, { FC, MouseEvent, useRef, useState } from 'react'
import styled from 'styled-components'

import { SingleSourceOrSourceSet } from '../../../../lib/products/images'

const ImageContainer = styled.div<{ isZoomed: boolean }>`
  width: 100%;
  height: auto;
  position: relative;
  display: block;
  line-height: 0;
  overflow: visible;
  @media (min-width: 992px) {
    cursor: ${({ isZoomed }) => (isZoomed ? 'none' : 'zoom-in')};
  }
`

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 4px;
  object-fit: contain;
  aspect-ratio: 1 / 1;
`

const MagnifyingLens = styled.div`
  position: absolute;
  z-index: 1;
  background: #e5e5e5 no-repeat;
  border: solid #ebebeb;
  box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  border-radius: 8px;
`

interface ImageWithMagnifierProps {
  source: SingleSourceOrSourceSet
  srcZoom: string
  magnifierWidth?: number
  magnifierHeight?: number
  magnifierBorderWidth?: number
  zoomFactor?: number
  alt?: string
  className?: string
}

const ImageWithMagnifier: FC<ImageWithMagnifierProps> = ({
  source,
  srcZoom,
  magnifierWidth = 450,
  magnifierHeight = 450,
  magnifierBorderWidth = 2,
  zoomFactor = 1.0,
  alt = '',
  className = '',
}) => {
  const imgRef = useRef<null | HTMLImageElement>(null)
  const [imgBounds, setImgBounds] = useState<DOMRect>()
  const [relX, setRelX] = useState(0.5) // Default to center of image
  const [relY, setRelY] = useState(0.5)
  const [showZoom, setShowZoom] = useState(false)
  const clamp = (v: number) => Math.min(1, Math.max(0, v))
  const getImgBounds = () => imgRef.current && setImgBounds(imgRef.current?.getBoundingClientRect())

  const closeZoom = () => setShowZoom(false)
  const toggleZoom = () => {
    // Only enable the zoom function on desktop
    if (window.innerWidth >= 992) setShowZoom((prev) => !prev)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const step = 0.05
    let x = relX,
      y = relY

    switch (e.key) {
      case 'ArrowUp':
        y = clamp(relY - step)
        e.preventDefault()
        break
      case 'ArrowDown':
        y = clamp(relY + step)
        e.preventDefault()
        break
      case 'ArrowLeft':
        x = clamp(relX - step)
        e.preventDefault()
        break
      case 'ArrowRight':
        x = clamp(relX + step)
        e.preventDefault()
        break
      case 'Enter':
      case ' ':
        toggleZoom()
        e.preventDefault()
        return
      default:
        return
    }

    setRelX(x)
    setRelY(y)
  }

  return (
    <ImageContainer
      tabIndex={0}
      isZoomed={showZoom}
      className={className}
      onClick={toggleZoom}
      onBlur={closeZoom}
      role="button"
      onKeyDown={handleKeyDown}
      onMouseEnter={() => getImgBounds()}
      onMouseMove={(e: MouseEvent) => {
        if (imgBounds) {
          const target = e.target as HTMLElement
          setRelX((e.clientX - imgBounds.left) / target.clientWidth)
          setRelY((e.clientY - imgBounds.top) / target.clientHeight)
        }
      }}
      onMouseLeave={closeZoom}
    >
      <Image
        ref={imgRef}
        aria-label="Product Image"
        {...(source._tag === 'SingleSource'
          ? { src: source.src }
          : { src: source.defaultSrc, srcSet: source.srcSet, sizes: source.sizes })}
        alt={alt ?? ''}
        onLoad={() => getImgBounds()}
      />
      {imgBounds && showZoom && (
        <MagnifyingLens
          style={{
            width: magnifierWidth,
            height: magnifierHeight,
            borderWidth: magnifierBorderWidth,
            backgroundImage: `url("${srcZoom}")`,
            left: `calc(${relX * 100}% - ${magnifierWidth / 2}px - ${magnifierBorderWidth}px)`,
            top: `calc(${relY * 100}% - ${magnifierHeight / 2}px - ${magnifierBorderWidth}px)`,
            backgroundPosition: `
              calc(${relX * 100}% + ${magnifierWidth / 2}px - ${relX * magnifierWidth}px)
              calc(${relY * 100}% + ${magnifierHeight / 2}px - ${relY * magnifierWidth}px)
            `,
            backgroundSize: `
              ${zoomFactor * imgBounds.width}%
              auto
            `,
          }}
        />
      )}
    </ImageContainer>
  )
}

export default ImageWithMagnifier
